/**
 * Shipping, returns, privacy, and terms.
 *
 * Design Bible 16 records the legal copy as an Operator Acceptance Gate item
 * that does not exist yet, and the WP04 packet asks for legal and return-policy
 * *placeholders* rather than invented text. So this page renders whatever the
 * server injected and, when nothing has been injected, says plainly that the
 * policy is not published — it does not fill the gap with plausible retail
 * boilerplate that nobody has approved and that would be legally meaningless.
 */

import type { ReactNode } from "react";
import { useStorefront } from "../app/StorefrontContext.js";
import { StatusChip } from "../components/primitives.js";

export function PoliciesView(): ReactNode {
  const { config } = useStorefront();
  const sections: readonly { readonly id: string; readonly title: string; readonly body: string }[] = [
    { id: "shipping", title: "Shipping", body: config.legal.shipping },
    { id: "returns", title: "Returns", body: config.legal.returns },
    { id: "privacy", title: "Privacy", body: config.legal.privacy },
    { id: "terms", title: "Terms", body: config.legal.terms }
  ];

  return (
    <div className="stack policies">
      <h1 className="t-display-l">Shipping, returns, and terms</h1>
      <div className="banner banner--accent">
        <StatusChip label="Placeholder" tone="accent" />
        <span>
          The wording below is whatever the shop has published. Where a policy has not been published
          yet, this page says so rather than inventing one.
        </span>
      </div>
      {sections.map((section) => (
        <section aria-labelledby={`policy-${section.id}`} key={section.id}>
          <h2 className="t-display-xs" id={`policy-${section.id}`}>
            {section.title}
          </h2>
          <p className="t-body-l">{section.body}</p>
        </section>
      ))}
      <p className="t-body-m">
        Every item is insured in transit and requires a signature on delivery. That is a property of
        how each order is packed and labelled, and it is recorded against the order rather than
        promised here.
      </p>
    </div>
  );
}
